import React, { memo, type FC } from "react";
import { ImgTwitter, ImgYoutube } from "~/src/assets/img";
import { Link } from "react-router-dom";
import DownloadAvacusApp from "./DownloadAvacusApp";
import AvacusLogoHeader from "./AvacusLogoHeader";
import { useTranslations } from "next-intl";
import { Divider } from "./ui/adapters";

interface FooterProps {
  showDivider?: boolean;
  logo?: React.ReactNode;
}

const Footer: FC<FooterProps> = ({ showDivider = true, logo }) => {
  const t = useTranslations();
  const year = new Date().getFullYear();

  return (
    <>
      <footer className="w-full mx-auto px-4 md:px-8 max-w-7xl lg:mx-auto print:mx-auto">
        {showDivider && <Divider className="my-6" />}
        <div className="w-full mb-9">
          {logo ?? <AvacusLogoHeader />}
          <div className="flex justify-between items-start mt-8">
            <div className="grid grid-flow-row gap-6 w-[50%] md:w-auto">
              <div className="leading-9 -mt-2 lg:mt-0 print:mt-0">
                <p className="app-text-sub2 md:app-text-body1 leading-8 md:leading-10">
                  50 Raffles Place #19-00
                </p>
                <p className="app-text-sub2 md:app-text-body1 leading-8 md:leading-10">
                  Singapore Land Tower Singapore (048623)
                </p>
              </div>
              <div className="flex justify-start items-center space-x-5 md:space-x-6">
                <a
                  href="https://youtube.com/@AvacusAppOfficial"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={ImgYoutube}
                    alt="Youtube"
                    className="size-7 md:size-8 object-contain"
                  />
                </a>
                <a
                  href="https://twitter.com/avacusapp"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={ImgTwitter}
                    alt="Twitter"
                    className="size-7 md:size-8 object-contain"
                  />
                </a>
              </div>
            </div>
            <div className="grid grid-flow-row md:grid-flow-row gap-8 w-[50%] md:w-auto">
              <div className="flex justify-end md:justify-start">
                <div className="block md:grid md:grid-flow-col space-y-6 md:space-y-0 md:gap-8">
                  <div className="grid grid-flow-row gap-2 md:gap-6 lg:gap-4 print:gap-4">
                    <p className="app-text-sub2 text-gray-second">
                      {t("web.company_name")}
                    </p>
                    <a
                      href="https://sowaka.io"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="app-text-body2 lg:app-text-body1 print:app-text-body1"
                    >
                      SOWAKA PTE. LTD.
                    </a>
                  </div>
                  <div className="grid grid-flow-row gap-2 md:gap-0 lg:gap-4 print:gap-4">
                    <p className="app-text-sub2 text-gray-second">
                      {t("web.established_date")}
                    </p>
                    <p className="app-text-body2 lg:app-text-body1 print:app-text-body1">
                      29/08/2022
                    </p>
                  </div>
                  <div className="grid grid-flow-row gap-2 md:gap-0 lg:gap-4 print:gap-4">
                    <p className="app-text-sub2 text-gray-second">
                      {t("web.registered_no")}
                    </p>
                    <p className="app-text-body2 lg:app-text-body1 print:app-text-body1">
                      202230387M
                    </p>
                  </div>
                </div>
              </div>
              <div className="hidden md:flex justify-end items-center">
                <DownloadAvacusApp />
              </div>
            </div>
          </div>
          <div className="flex justify-start items-center md:hidden mt-8">
            <DownloadAvacusApp />
          </div>
          <div className="md:flex md:justify-between md:items-center mt-8 md:mt-9">
            <div className="text-gray-second app-text-sub2 md:app-text-body1 leading-8 md:leading-10">
              {t("web.copyright", { year })}
            </div>
            <div className="flex gap-4 md:gap-8 mt-4 md:mt-0 app-text-body2-bold md:app-text-body1">
              <Link to="/terms" target="_blank">
                {t("web.terms")}
              </Link>
              <Link to="/policy" target="_blank">
                {t("web.policy")}
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default memo(Footer);
